import type { TileType } from '@shared/board/types';
import type { BoardGraph } from '@shared/board/graph';
import type { DistanceCache } from '@shared/board/distance-cache';
import type { GameState } from './types';
import { modifyKeys } from './economy';
import { applyDamage, applyDamageWithKeyDrop } from './damage';
import { applyHeal } from './healing';
import { resolveDroppedKeyPickupAtNode } from './dropped-keys';
import { handleDeath } from './death';
import {
  getAnchorOwner,
  claimSoulAnchor,
  randomSoulAnchorMode,
  triggerSoulAnchor,
} from './soul-anchor';
import { canPurchaseTrophy, purchaseTrophy } from './treasury';
import { mulberry32 } from './dice';
import { allItems } from './items';
import { tryBlockWithStoneSkin } from './stone-skin-block';
import { grantPlayerItem } from './inventory';

/**
 * Handler function signature for tile landing effects.
 * Called when a player lands on a tile, with access to game state, board graph, and distance cache.
 */
export type LandingEffectHandler = (
  state: GameState,
  playerId: string,
  nodeId: string,
  graph: BoardGraph,
  distanceCache: DistanceCache,
) => void;

/**
 * Registry mapping tile types to their landing effect handlers.
 * Handlers are called when a player lands on a tile (end of movement).
 */
const registry = new Map<TileType, LandingEffectHandler>();

const WEAPONS_CACHE_ITEM_IDS = ['cursed_relic', 'orbital_cannon', 'soul_duel'] as const;

/**
 * Register a landing effect handler for a tile type.
 * Replaces any previous handler for the same tile type.
 *
 * @param tileType - The tile type to register
 * @param handler - The effect handler function
 */
export function registerLandingEffect(tileType: TileType, handler: LandingEffectHandler): void {
  registry.set(tileType, handler);
}

/**
 * Execute the landing effect for a tile, if one is registered.
 * Looks up the tile type from the board graph and calls the registered handler.
 * If the node does not exist or has no registered handler, this is a no-op.
 *
 * @param state - Game state to mutate
 * @param playerId - Player landing on the tile
 * @param nodeId - The tile node ID
 * @param graph - Board graph for tile type lookup
 * @param distanceCache - Pre-computed distance cache
 */
export function executeLandingEffect(
  state: GameState,
  playerId: string,
  nodeId: string,
  graph: BoardGraph,
  distanceCache: DistanceCache,
): void {
  const node = graph.getNode(nodeId);
  if (!node) return;

  pickUpDroppedKeys(state, playerId, nodeId);

  if (nodeId === state.treasureChestNodeId) {
    handleTreasureChestLanding(state, playerId, graph);
    return;
  }

  if (nodeId === state.weaponsCacheNodeId) {
    handleWeaponsCacheLanding(state, playerId, graph);
    return;
  }

  const handler = registry.get(node.type);
  if (handler) {
    handler(state, playerId, nodeId, graph, distanceCache);
  }
}

export function executePassiveLandingEffect(
  state: GameState,
  playerId: string,
  nodeId: string,
  graph: BoardGraph,
  distanceCache: DistanceCache,
): void {
  const node = graph.getNode(nodeId);
  if (!node) return;
  if (nodeId === state.treasureChestNodeId || nodeId === state.weaponsCacheNodeId) return;

  const handler = registry.get(node.type);
  if (handler) {
    handler(state, playerId, nodeId, graph, distanceCache);
  }
}

/**
 * Get all tile types that have registered handlers.
 * Useful for testing and debugging.
 *
 * @returns Array of registered tile types
 */
export function getAllRegisteredTileTypes(): TileType[] {
  return Array.from(registry.keys());
}

/**
 * Pick up dropped keys from a tile and transfer to player.
 * If keys are present at the node, transfers them to the player via modifyKeys
 * and removes the entry from the droppedKeys map.
 * Spec §3: Dropped keys can be picked up by any player.
 *
 * @param state - Game state to mutate
 * @param playerId - Player picking up the keys
 * @param nodeId - Node ID where keys are dropped
 */
export function pickUpDroppedKeys(state: GameState, playerId: string, nodeId: string): number {
  if (!state.players.has(playerId)) throw new Error(`Player not found: ${playerId}`);
  return (
    resolveDroppedKeyPickupAtNode(state, nodeId).find(
      (allocation) => allocation.playerId === playerId,
    )?.amount ?? 0
  );
}

/**
 * Check if the game is in the last three rounds.
 * Spec §4: Last-3-Rounds Comeback Chaos rule.
 * Returns true when currentRound > maxRounds - 3.
 * Returns false for unlimited rounds (maxRounds === 0).
 *
 * @param state - Game state
 * @returns true if in the last 3 rounds, false otherwise
 */
export function isLastThreeRounds(state: GameState): boolean {
  return state.maxRounds > 0 && state.round > state.maxRounds - 3;
}

// ===== Default Handler Implementations =====

/**
 * Start tile handler: no-op.
 * Spec §3: "Landing on start tile has no effect."
 */
function handleStartTile(): void {
  // no-op
}

/**
 * Gold tile handler: grant +5 keys to the player.
 * Spec §3: "Landing on gold tile grants 5 keys."
 */
function handleGoldTile(state: GameState, playerId: string): void {
  modifyKeys(state, playerId, 5, 'gold_tile');
}

/**
 * Heal tile handler: restore up to 15 HP to the player.
 * Spec §3: "Landing on heal tile grants 15 HP (capped at max 30)."
 */
function handleHealTile(
  state: GameState,
  playerId: string,
  _nodeId: string,
  _graph: BoardGraph,
  _distanceCache: DistanceCache,
): void {
  if (!state.players.has(playerId)) return;
  applyHeal(state, playerId, 15, 'heal_tile', { allowOverheal: false });
}

/**
 * Graveyard handler: no-op on landing.
 * Graveyard effects are only triggered during respawn, not normal movement.
 * Spec §3: "Graveyard is a respawn point; no landing effect."
 */
function handleGraveyardTile(): void {
  // no-op
}

/**
 * Hazard tile handler: apply 5 HP damage to the player.
 * Spec §3: "Landing on hazard tile deals 5 damage."
 * If the damage is lethal, the player dies and is respawned.
 * If Pinata modifier is active, ALL keys drop on damage.
 * If One Hit Kill modifier is active, damage is overridden to 30 (instant kill).
 */
function handleHazardTile(
  state: GameState,
  playerId: string,
  _nodeId: string,
  graph: BoardGraph,
  distanceCache: DistanceCache,
): void {
  if (tryBlockWithStoneSkin(state, playerId, { actorId: null, sourceId: 'hazard_tile' })) {
    return; // Skip damage + key drop
  }

  // Hazard tiles explicitly cause both HP damage and key loss.
  const result = applyDamageWithKeyDrop(state, playerId, 5, 'hazard_tile', null);

  // If lethal, handle death and respawn
  if (result.died) {
    handleDeath(state, playerId, graph, distanceCache);
  }
}

/**
 * Pick a random item id from the eligible pool.
 * Uses the same deterministic mulberry32 rngState pattern as dice and dynamic spawns.
 */
function pickRandomItemId(state: GameState, eligibleItemIds: readonly string[]): string | null {
  if (eligibleItemIds.length === 0) return null;

  const rng = mulberry32(state.rngState);
  state.rngState = rng.nextState;

  const index = Math.floor(rng.value * eligibleItemIds.length);
  return eligibleItemIds[index] ?? null;
}

export function hasInventorySpace(state: GameState, playerItems: readonly string[]): boolean {
  void state;
  void playerItems;
  return true;
}

function grantRandomItem(
  state: GameState,
  playerId: string,
  eligibleItemIds: readonly string[],
  source: 'item_tile' | 'weapons_cache',
): void {
  const player = state.players.get(playerId);
  if (!player || !hasInventorySpace(state, player.items)) return;

  const itemId = pickRandomItemId(state, eligibleItemIds);
  if (itemId === null) return;

  grantPlayerItem(state, playerId, itemId, source);
}

function standardItemIds(): string[] {
  return allItems()
    .filter((item) => item.category !== 'armory')
    .map((item) => item.id);
}

export function weaponsCacheItemIds(): string[] {
  const registeredItemIds = new Set(allItems().map((item) => item.id));
  return WEAPONS_CACHE_ITEM_IDS.filter((itemId) => registeredItemIds.has(itemId));
}

export function relocateWeaponsCache(
  state: GameState,
  graph: BoardGraph,
  extraExcludedNodeIds: ReadonlySet<string> = new Set(),
): void {
  const goalNodes = graph.getGoalNodes();
  const eligible = goalNodes.filter(
    (n) =>
      n.id !== state.treasureChestNodeId &&
      n.id !== state.weaponsCacheNodeId &&
      !extraExcludedNodeIds.has(n.id),
  );

  state.weaponsCacheRespawnIn = 0;
  if (eligible.length === 0) {
    state.weaponsCacheNodeId = null;
    return;
  }

  const rng = mulberry32(state.rngState);
  state.rngState = rng.nextState;

  const index = Math.floor(rng.value * eligible.length);
  state.weaponsCacheNodeId = eligible[index]?.id ?? null;
}

export function claimWeaponsCacheItem(
  state: GameState,
  playerId: string,
  itemId: string,
  graph: BoardGraph,
): boolean {
  const player = state.players.get(playerId);
  if (!player || !hasInventorySpace(state, player.items)) return false;
  if (!weaponsCacheItemIds().includes(itemId)) return false;

  const claimedNodeId = state.weaponsCacheNodeId;
  grantPlayerItem(state, playerId, itemId, 'weapons_cache');
  const excludedNodeIds = new Set<string>();
  if (claimedNodeId != null) {
    excludedNodeIds.add(claimedNodeId);
    for (const neighbor of graph.getNeighbors(claimedNodeId)) {
      excludedNodeIds.add(neighbor.node.id);
    }
  }
  relocateWeaponsCache(state, graph, excludedNodeIds);
  return true;
}

/**
 * Item tile handler: grant one random standard item.
 * Spec §4: Item tiles grant a random standard item.
 * Spec §4: Last-3-Rounds Comeback Chaos rule doubles item grants during final 3 rounds.
 */
function handleItemTile(state: GameState, playerId: string): void {
  grantRandomItem(state, playerId, standardItemIds(), 'item_tile');
  // TODO: Apply Last-3-Rounds Comeback Chaos double item grants for regular item tiles.
}

/**
 * Event tile handler: stub for map-specific events.
 * Event tiles trigger map-specific events (e.g., Sky Quake on Skyforge).
 * Map-specific handlers are deferred to a later plan; this is a no-op stub.
 */
function handleEventTile(): void {
  // Stub: map-specific event handlers deferred to later plan
}

/**
 * Pathing tile handler: no-op.
 * Pathing tiles are not landable (movement skips them).
 * Registered defensively so the registry is complete for all board tile types.
 */
function handlePathingTile(): void {
  // no-op
}

function handleNoopSpecialTile(): void {
  // no-op
}

/**
 * Dynamic treasure chest handler: attempt to purchase a trophy.
 * If the player can afford the trophy (keys >= trophyCost), they purchase it and the chest relocates.
 */
function handleTreasureChestLanding(state: GameState, playerId: string, graph: BoardGraph): void {
  if (canPurchaseTrophy(state, playerId)) {
    purchaseTrophy(state, playerId, graph);
  }
}

/**
 * Dynamic weapons cache handler: grant an armory item and relocate the cache.
 */
function handleWeaponsCacheLanding(state: GameState, playerId: string, graph: BoardGraph): void {
  const itemId = pickRandomItemId(state, weaponsCacheItemIds());
  if (itemId === null) return;
  claimWeaponsCacheItem(state, playerId, itemId, graph);
}

// ===== Weapons Cache Respawn Timer =====

/**
 * Tick the weapons cache respawn timer.
 * Called once per turn during the game loop.
 *
 * Behavior:
 * - If cache is active (weaponsCacheNodeId !== null), no-op.
 * - If respawn timer is already at 0, no-op.
 * - Otherwise, decrement weaponsCacheRespawnIn.
 * - When respawnIn reaches 0, relocate the cache to a random eligible goal node
 *   (via mulberry32 RNG, excluding treasureChestNodeId).
 *
 * @param state - Mutable GameState. state.rngState and state.weaponsCacheNodeId are mutated.
 * @param graph - Board graph for goal node lookup.
 */
export function tickWeaponsCacheRespawn(state: GameState, graph: BoardGraph): void {
  // If cache is active, no tick needed
  if (state.weaponsCacheNodeId !== null) return;

  // If respawn timer is already at 0, no tick needed
  if (state.weaponsCacheRespawnIn <= 0) return;

  // Decrement the timer
  state.weaponsCacheRespawnIn--;

  // When timer reaches 0, relocate cache to a random eligible goal node
  if (state.weaponsCacheRespawnIn === 0) {
    const goalNodes = graph.getGoalNodes();
    const eligible = goalNodes.filter((n) => n.id !== state.treasureChestNodeId);

    if (eligible.length > 0) {
      // Use mulberry32 RNG to select a random eligible node
      const rng = mulberry32(state.rngState);
      state.rngState = rng.nextState;

      const index = Math.floor(rng.value * eligible.length);
      state.weaponsCacheNodeId = eligible[index].id;
    }
  }
}

// ===== Registry Initialization =====

/**
 * Initialize default landing effect handlers.
 * Called at module load time.
 */
registerLandingEffect('start', handleStartTile);
registerLandingEffect('gold', handleGoldTile);
registerLandingEffect('graveyard', handleGraveyardTile);
registerLandingEffect('heal', handleHealTile);
registerLandingEffect('hazard', handleHazardTile);
registerLandingEffect('item', handleItemTile);
registerLandingEffect('event', handleEventTile);
registerLandingEffect('key_shop', handleNoopSpecialTile);
registerLandingEffect('weapons_cache', handleNoopSpecialTile);
registerLandingEffect('mystery_box', handleNoopSpecialTile);
registerLandingEffect('pathing', handlePathingTile);

/**
 * Soul anchor handler: claim, trigger, or no-op depending on ownership.
 * Spec §3: First player to land claims the anchor (and adjacent soul_anchor nodes).
 *          Subsequent non-owner landings trigger the steal/damage effect.
 *          Owner landing on their own anchor is a no-op.
 */
registerLandingEffect('soul_anchor', (state, playerId, nodeId, graph, distanceCache) => {
  const owner = getAnchorOwner(state, nodeId);
  if (owner === null) {
    claimSoulAnchor(state, playerId, nodeId, graph, randomSoulAnchorMode(state));
  } else if (owner !== playerId) {
    const result = triggerSoulAnchor(state, playerId, owner, nodeId);
    if (result.died) {
      handleDeath(state, playerId, graph, distanceCache);
    }
  }
  // If owner === playerId: no-op (landing on own anchor)
});

/**
 * Resolve a battle-tile encounter via the flag-off fallback (50/50 coin flip → loser
 * takes 5 HP). Future plans (Plan 25-32 era) will add a `battle` TileType + a landing
 * handler that calls this helper from the flag-off branch and `battle_minigame_start`
 * from the flag-on branch.
 *
 * RNG seam: Math.random() (per spec §4.3 — same convention as Soul Duel fallback).
 *
 * Effects:
 *   - 50/50 coin flip via Math.random() < 0.5
 *   - Loser takes 5 HP via applyDamage(state, loserId, 5, 'battle_tile_fallback', winnerId)
 *   - No event emitted by this helper itself; applyDamage emits the damage event.
 *
 * Caller MUST handle resulting death+respawn flow via handleDeath() if applyDamage
 * returns {died: true}. (Battle tile damage of 5 is non-lethal except at HP <= 5.)
 *
 * @param state - Mutable GameState
 * @param playerAId - First player on the tile (typically the landing player)
 * @param playerBId - Second player on the tile (typically the player already on it)
 * @returns { winnerId, loserId, damageResult } — winnerId/loserId reflect the coin flip;
 *          damageResult is the return of applyDamage (so caller can check .died).
 * @throws Error if either player not found.
 */
export function resolveBattleTileFallback(
  state: GameState,
  playerAId: string,
  playerBId: string,
): { winnerId: string; loserId: string; damageResult: { died: boolean; keysDropped: number } } {
  if (!state.players.has(playerAId)) throw new Error(`Battle tile: player ${playerAId} not found`);
  if (!state.players.has(playerBId)) throw new Error(`Battle tile: player ${playerBId} not found`);

  const aWins = Math.random() < 0.5;
  const winnerId = aWins ? playerAId : playerBId;
  const loserId = aWins ? playerBId : playerAId;
  const damageResult = applyDamage(state, loserId, 5, 'battle_tile_fallback', winnerId);

  return { winnerId, loserId, damageResult };
}
